import type { ProjectPlan, ProjectRecord, TileStatus } from "@/types/api";
import { coverImage, recordImages } from "@/lib/record-images";

/**
 * Key derivation and download extraction for the offline asset cache.
 *
 * Shared by the cache's writers (`sync.ts` pin/resync, the backfill subscriber)
 * and its readers (`OfflineImage` call sites) so a blob is always stored and
 * looked up under an identical key. See [[offline-asset-cache]].
 */

/** A blob to download into the offline asset cache: its stable key and source URL. */
export interface AssetDownload {
  key: string;
  url: string;
}

/**
 * Offline asset-cache key for a record's thumbnail — image id plus a content
 * version (`updated_at` as epoch milliseconds).
 *
 * The version is required: the backend replaces a record's image **in place**
 * on a markup re-edit — the `ProjectImage` id stays the same while the bytes
 * change — so an id-only key would let the idempotent cache serve the pre-edit
 * photo forever. Versioning by `updated_at` makes a re-edit yield a new key, so
 * the fresh image is fetched. See [[offline-asset-cache]].
 */
export function thumbAssetKey(record: ProjectRecord): string {
  // The cover is resolved at read time (`images[0]` under v2.0, else the legacy
  // `image`) — never a separately-stored field. A record with no cover (no
  // `images`, no `image`) yields `""` so `OfflineImage` renders no `src`
  // rather than keying off a missing id.
  // `updated_at` is a non-null ISO string per the API contract; the `0`
  // fallback only guards a malformed server value, never an expected null.
  // Delegates to the per-image helper so the cover photo and every additional
  // photo of a multi-photo record share one key shape.
  const cover = coverImage(record);
  if (!cover) return "";
  return thumbAssetKeyFor(cover.id, record.updated_at);
}

/**
 * Offline asset-cache key for a single image of a record by image id + the
 * record's content version (`updated_at`). The cover photo of a record yields
 * exactly `thumbAssetKey(record)`; the additional photos of a multi-photo
 * record (see [[record-images]]) each get their own key under the same shape,
 * so the grid and fullscreen carousel resolve every tile from the cache once
 * `images[]` ships. See [[offline-asset-cache]].
 */
export function thumbAssetKeyFor(imageId: string, updatedAt: string): string {
  const parsed = Date.parse(updatedAt);
  const version = Number.isFinite(parsed) ? parsed : 0;
  return `thumb:${imageId}:${version}`;
}

/**
 * Offline asset-cache key for a plan revision's image.
 *
 * A revision id alone is NOT content-safe anymore: the tile pipeline
 * replaces the interim 2048px upload with the server's 6144px fallback
 * JPEG **in place** (same revision) when `tile_status` flips to `ready` —
 * an id-only key would pin devices to the interim forever.
 *
 * Chosen version source: `tile_status`, normalized to a binary
 * ready/not-ready. `ProjectPlanRevision` exposes no `updated_at`
 * (src/types/api.ts — id/plan/name/image_url/thumbnail_url/created_by/
 * created_at only, and the tiles contract adds only tile_status/tiles/
 * tile_error), so a timestamp version is impossible without an API change.
 * Binary normalization beats raw status interpolation: the bytes only
 * change at the `ready` transition, so pending→processing must NOT roll
 * the key (it would re-download the identical interim mid-pipeline), and
 * legacy/absent keeps the historical 2-segment key so existing pinned
 * caches survive the deploy untouched. `resyncPinnedProjects`' stale-key
 * diff (src/offline/sync.ts) evicts the old key only after the new blob
 * lands — no code change needed there.
 *
 * `tileStatus` is REQUIRED (not optional): an omitted status would silently
 * compute the un-suffixed key for a ready revision while resync stored the
 * blob under `:tiled` — an offline-only broken image with no error. Callers
 * must pass the revision's `tile_status`; `null`/`undefined` are valid
 * VALUES (legacy revision / payload cached before the tiles deploy) but the
 * argument itself cannot be left out.
 */
export function planAssetKey(
  planId: string,
  revisionId: string,
  tileStatus: TileStatus | null | undefined,
): string {
  const base = `plan:${planId}:${revisionId}`;
  return tileStatus === "ready" ? `${base}:tiled` : base;
}

/**
 * Extract the plan id from a plan asset key — both shapes: the legacy
 * 2-segment `plan:{planId}:{revisionId}` and the tiled
 * `plan:{planId}:{revisionId}:tiled`.
 */
export function planIdFromAssetKey(key: string): string | null {
  if (!key.startsWith("plan:")) return null;
  const parts = key.split(":");
  return parts[1] ?? null;
}

/**
 * The thumbnail downloads needed to cache a set of records offline — every
 * photo of every record, not just the cover. Iterates `recordImages(record)`
 * (the [[record-images]] seam) and keys each photo under
 * `thumbAssetKeyFor(img.id, record.updated_at)`. The cover (`images[0]`) yields
 * exactly `thumbAssetKey(record)`, so existing cache entries are unchanged.
 * No-op today (a record carries one photo); forward-correct for the v2.0
 * `images[]` read so all photos in the multi-photo grid render offline.
 */
export function recordDownloadsFrom(records: ProjectRecord[]): AssetDownload[] {
  const downloads: AssetDownload[] = [];
  for (const record of records) {
    for (const img of recordImages(record)) {
      const url = img.thumbnail ?? img.image;
      if (!url) continue;
      downloads.push({
        key: thumbAssetKeyFor(img.id, record.updated_at),
        url,
      });
    }
  }
  return downloads;
}

/** The image downloads needed to cache a set of plans offline. */
export function planDownloadsFrom(plans: ProjectPlan[]): AssetDownload[] {
  const downloads: AssetDownload[] = [];
  for (const plan of plans) {
    const revision = plan.latest_revision;
    if (!revision || !revision.image_url) continue;
    downloads.push({
      key: planAssetKey(plan.id, revision.id, revision.tile_status),
      url: revision.image_url,
    });
  }
  return downloads;
}
